import type { TExample } from '@atlas/uicorn/preview/modules';
import { useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import ContextMenu, {
    type TContextMenuProps,
    type TContextMenuRef,
    type TContextMenuItem,
    type TContextMenuSearch,
} from '@atlas/uicorn/lib/Control/ContextMenu';
import useRefCallback from 'use-ref-callback';
import hotkey from '@atlas/tool/box/event/hotkey';
import useControlledState from '@atlas/tool/box/hooks/useControlledState';
import Text from '@atlas/uicorn/lib/Display/Text';
import ChevronIcon from '@atlas/uicorn/lib/Icon/ChevronIcon';
import { FormContext } from '@atlas/uicorn/lib/Control/Form';
import IconButton from '@atlas/uicorn/lib/Control/IconButton';
import CrossIcon from '@atlas/uicorn/lib/Icon/CrossIcon';
import useLastRef from '@atlas/tool/box/hooks/useLastRef';
import window from '@atlas/tool/box/browser/window';
import FormItem from '@atlas/uicorn/lib/Control/FormItem';
import cn from 'classnames';

const Select = React.memo(
    React.forwardRef(function Select<TOption extends TSelectOption = TSelectOption>(
        props: TSelectProps<TOption>,
        ref: React.ForwardedRef<TSelectRef>,
    ): React.ReactElement {
        const {
            value: propsValue,
            defaultValue,
            onChange,
            placeholder,
            open: propsOpen,
            defaultOpen,
            onOpen,
            disabled: propsDisabled,
            allowClear,
            className,
            style,
            options: propsOptions,
            dropdown,
            search,
            ...menuProps
        } = props;

        const { disabled: contextDisabled } = useContext(FormContext);
        const disabled = propsDisabled ?? contextDisabled;

        const status = FormItem.useStatus();

        const [element, setElement] = useState<HTMLDivElement | null>(null);
        const elementRef = useLastRef(element);

        const [minMenuWidth, setMinMenuWidth] = useState(0);

        const updateMinMenuWidth = useRefCallback(() => setMinMenuWidth(element?.offsetWidth ?? 0));

        const [menuRef, setMenuRef] = useState<TContextMenuRef | null>(null);

        const lastSelectedOptionRef = useRef<Partial<TOption>>();

        const normalizedValue = useMemo(
            () => (typeof propsValue !== 'undefined' ? ({ key: propsValue } as TOption) : undefined),
            [propsValue],
        );

        const normalizedDefaultValue = useMemo(
            () => (typeof defaultValue !== 'undefined' ? ({ key: defaultValue } as TOption) : undefined),
            [defaultValue],
        );
        const [options, setOptions] = useState(propsOptions);
        const useSearchResultAsOptions = !propsOptions;

        const [open, setOpen] = useControlledState(propsOpen, onOpen, defaultOpen);
        const [value, setValue] = useControlledState<Partial<TOption> | undefined>(
            normalizedValue,
            (option) => {
                if (!onChange) return;
                if (typeof option?.key === 'undefined') onChange(undefined, undefined);
                else onChange(option.key, option as TOption);
                lastSelectedOptionRef.current = option;
            },
            normalizedDefaultValue,
        );
        const selected = useMemo(() => (typeof value !== 'undefined' ? [value as any as TOption] : []), [value]);

        const valueOption = useMemo(
            () => (value && options && options.find((item) => item.key === value.key)) || value,
            [value, options],
        );

        const [forceSearch, setForceSearch] = useState(false);
        useEffect(() => {
            if (open) return;
            setForceSearch(false);
        }, [open]);

        const preparedSearch = useMemo(
            () => search ?? (forceSearch || (options && options.length > 4)),
            [search, forceSearch, options],
        );

        useEffect(() => propsOptions && setOptions(propsOptions), [propsOptions, setOptions]);

        useEffect(() => {
            async function resolveOptions() {
                if (valueOption && useSearchResultAsOptions && typeof preparedSearch === 'function') {
                    const valueExists = options?.find((item) => item.key === valueOption.key);
                    const query = lastSelectedOptionRef?.current?.label || '';

                    if (!valueExists) {
                        const resolvedOptions = await preparedSearch(query);
                        setOptions(resolvedOptions);
                    }
                }
            }

            resolveOptions();
        }, [valueOption, useSearchResultAsOptions, options, preparedSearch]);

        useEffect(() => {
            updateMinMenuWidth();

            let timeoutId: ReturnType<typeof setTimeout> | null = null;
            const listener = () => {
                if (timeoutId !== null) clearTimeout(timeoutId);
                timeoutId = setTimeout(updateMinMenuWidth, 100);
            };
            window?.addEventListener('resize', listener);
            return () => window?.removeEventListener('resize', listener);
        }, [element, value, style, className]);

        useImperativeHandle(
            ref,
            () => ({
                ...menuRef,
                focus: () => elementRef.current?.focus(),
                blur: () => elementRef.current?.blur(),
            }),
            [menuRef, elementRef],
        );

        const handleKeyDown = useRefCallback<React.KeyboardEventHandler<HTMLDivElement>>((event) => {
            if (spaceHotkey.matches(event) || enterHotkey.matches(event)) {
                event.preventDefault();
                menuRef?.toggle();
                return;
            }

            if (upHotkey.matches(event) || downHotkey.matches(event)) {
                event.preventDefault();
                menuRef?.open();
                return;
            }

            if (search !== false && !event.ctrlKey && !event.metaKey && alphabetSymbolRegex.test(event.key)) {
                menuRef?.open();
                setForceSearch(true);
                return;
            }
        });

        const handleChoose = useRefCallback(setValue);

        const handleClear = useRefCallback((event: React.MouseEvent) => {
            event.preventDefault();
            setValue(undefined);
            setOpen(false);
        });

        const typeWorkaround: any = { $minWidth: minMenuWidth };

        return (
            <SSelectOptionsMenu
                {...menuProps}
                items={options}
                search={preparedSearch}
                open={disabled ? false : open}
                onOpenChange={disabled ? undefined : setOpen}
                selected={selected}
                onChoose={handleChoose}
                {...typeWorkaround}
                ref={setMenuRef}
            >
                <SSelect
                    className={cn(className, 'ant-input', {
                        'ant-input-status-error': status === 'error',
                    })}
                    style={style}
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    data-open={open}
                    data-disabled={disabled}
                    role="listbox"
                    ref={setElement}
                >
                    {valueOption?.icon}
                    <SValueText children={valueOption?.label ?? placeholder} ellipsis $isPlaceholder={!valueOption} />
                    {valueOption && allowClear ? (
                        <SSelectClearButton
                            onClick={handleClear}
                            tabIndex={-1}
                            icon={
                                <>
                                    <SSelectClearIcon />
                                    <SSelectDropdownIndicator
                                        as={dropdown}
                                        opened={open && !disabled}
                                        disabled={disabled}
                                    />
                                </>
                            }
                        />
                    ) : (
                        <SSelectToggleIcon as={dropdown} opened={open && !disabled} disabled={disabled} />
                    )}
                </SSelect>
            </SSelectOptionsMenu>
        );
    }),
);

export default Select;

// #region Helpers

export const spaceHotkey = hotkey('space', 'hidden');
export const enterHotkey = hotkey('enter', 'hidden');
export const upHotkey = hotkey('up', 'hidden');
export const downHotkey = hotkey('down', 'hidden');

export const alphabetSymbolRegex = /^[a-zA-Z]$/;

export function SelectDropdownIndicator({
    className,
    opened,
}: React.ComponentProps<NonNullable<TSelectProps['dropdown']>>) {
    return <ChevronIcon className={className} dir={opened ? 'up' : 'down'} />;
}

// #endregion

// #region Styles

export const SSelectClearIcon = styled(CrossIcon)``;

export const SSelectToggleIcon = styled(SelectDropdownIndicator)`
    flex-shrink: 0;
    margin-left: auto;
`;

export const SSelectDropdownIndicator = styled(SelectDropdownIndicator)``;

export const SSelectClearButton = styled(IconButton).attrs({ tooltip: '' })`
    flex-shrink: 0;
    margin-left: auto;
`;

const SValueText = styled(Text)<{ $isPlaceholder: boolean }>`
    color: ${(p) => (p.$isPlaceholder ? 'var(--input-placeholder-color)' : p.theme.color.grey9)};
`;

export const SSelect = styled.div`
    ${(p) => p.theme.patch.input()}
    --select-value-gap: 0.6rem;

    display: inline-flex;
    align-items: center;
    gap: var(--select-value-gap);
    user-select: none;
    cursor: pointer;

    max-width: 100%;

    ${SSelectClearIcon} {
        scale: 0.8;
    }

    &:is(:not(:hover, [data-open='true'])) {
        ${SSelectClearIcon} {
            display: none;
        }
    }

    &:is(:hover, [data-open='true']) {
        ${SSelectClearIcon} + ${SSelectDropdownIndicator} {
            display: none;
        }
    }
`;

export const SSelectOptionsMenu = styled(ContextMenu)<{ $minWidth: number }>`
    min-width: ${(p) => p.$minWidth}px;
` as typeof ContextMenu;

// #endregion

// #region Types

export type TSelectRef =
    | {
          focus: () => void;
          blur: () => void;
      }
    | (TContextMenuRef & {
          focus: () => void;
          blur: () => void;
      });

export type TSelectOption = TContextMenuItem;

export type TSelectDropdownProps = { className?: string; opened: boolean; disabled: boolean };

export type TSelectProps<TOption extends TSelectOption = TSelectOption> = Omit<
    TContextMenuProps<TOption>,
    'onChange' | 'defaultValue' | 'items'
> & {
    options?: readonly TOption[]; // ✅ Works for both mutable and readonly arrays
    value?: TOption['key'];
    defaultValue?: TOption['key'];
    onChange?: ((value: TOption['key'], option: TOption) => void) & ((value: undefined, option: undefined) => void);
    placeholder?: string;
    disabled?: boolean;
    allowClear?: boolean;
    dropdown?: React.ComponentType<TSelectDropdownProps>;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        const options = useMemo<TSelectOption[]>(
            () => [
                { key: 'a', label: 'Option A', icon: '1️⃣' },
                { key: 'b', label: 'B is an option', icon: '2️⃣' },
                { key: 'c', label: 'Option C', icon: '3️⃣' },
                { key: 'd', label: 'Option D', icon: '4️⃣' },
                { key: 'e', label: 'Option E', icon: '5️⃣' },
                { key: 'f', label: 'Option F', icon: '6️⃣' },
                { key: 'g', label: 'Option G', icon: '7️⃣' },
                { key: 'h', label: 'Option H', icon: '8️⃣' },
                { key: 'i', label: 'Option I', icon: '9️⃣' },
                { key: 'j', label: 'Option J', icon: '🔟' },
            ],
            [],
        );

        const search = useMemo<boolean | TContextMenuSearch>(() => {
            if (props.search === 'Default' || typeof props.search === 'undefined') return true;

            if (props.search === 'Custom: Case sensitive') {
                return (query: string) => {
                    props.searchCallback(query);
                    return options.filter((option) =>
                        typeof option.label === 'string' ? option.label.includes(query) : false,
                    );
                };
            }

            if (props.search === 'Async data') {
                return (query: string) => {
                    props.searchCallback(query);

                    return new Promise((resolve) =>
                        setTimeout(
                            resolve,
                            1e3,
                            options
                                .filter((option) =>
                                    typeof option.label === 'string'
                                        ? option.label.toLowerCase().includes(query.toLowerCase())
                                        : false,
                                )
                                .slice(0, 5),
                        ),
                    );
                };
            }

            return false;
        }, [props.search, options]);

        // Props not initialized yet
        if (!props.search) return null;

        const dataProps = typeof search === 'function' ? { search } : { search, options };

        return (
            <Select
                key={props.defaultValue}
                defaultValue={
                    props.defaultValue === 'Option A' ? 'a' : props.defaultValue === 'Option B' ? 'b' : undefined
                }
                placeholder={props.placeholder}
                disabled={props.disabled}
                allowClear={props.allowClear}
                onChange={props.onChange}
                style={props.fixedWidth ? { width: `${props.fixedWidth}px` } : undefined}
                {...dataProps}
            />
        );
    },
    controls: {
        placeholder: 'string',
        disabled: 'boolean',
        search: ['Default', 'Custom: Case sensitive', 'Async data', 'No'],
        allowClear: 'boolean',
        fixedWidth: { type: 'number', initial: 400, connected: false },
        defaultValue: ['Not set', 'Option A', 'Option B'],
    },
    handlers: {
        onChange: 'console',
        searchCallback: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System----2.0?type=design&node-id=52-5474&mode=design&t=8rXmz6Q0cKMTeQF5-0',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect, vi, beforeAll, afterEach } = import.meta.vitest;
    describe('Select', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        beforeAll(function mockAddEventListener() {
            vi.mock('@atlas/tool/box/browser/window', () => {
                // Could not reset mock that is set in a different file
                return { default: {} };
            });
            if (window) {
                Object.assign(window, { addEventListener: vi.fn() });
                Object.assign(window, { removeEventListener: vi.fn() });
            }
        });

        afterEach(function resetState() {
            vi.resetAllMocks();
        });

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
